import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Link, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { ReceiptService } from '../../services/receipt.service';
import {
  SalesInvoice, SalesInvoiceStatus, SALES_INVOICE_STATUS_COLORS,
} from '../../interfaces/sales/salesInvoice.interface';
import { Receipt } from '../../interfaces/sales/receipt.interface';
import RecordReceiptDialog from './RecordReceiptDialog';
import { useToast } from '../../context/ToastContext';

// ReverseConfirmDialog props: open, entityLabel, consequence, onCancel, onConfirm, loading?
// It does NOT have a reason input — we collect voidReason via our own confirm dialog.

const STATUS_COLORS = SALES_INVOICE_STATUS_COLORS;

const SalesInvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [invoice, setInvoice] = useState<SalesInvoice | null>(null);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);

  // Issue dialog
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issuing, setIssuing] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Void dialog state (custom because ReverseConfirmDialog has no reason field)
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  // Record receipt dialog
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);

  // Email in-flight
  const [emailing, setEmailing] = useState(false);

  // PDF download in-flight
  const [downloading, setDownloading] = useState(false);

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const [inv, recs] = await Promise.all([
        SalesInvoiceService.getById(id),
        ReceiptService.listByInvoice(id),
      ]);
      setInvoice(inv);
      setReceipts(recs);
    } catch (e) {
      toast.showApiError(e, 'Failed to load invoice');
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIssue = async () => {
    if (!invoice) return;
    setIssuing(true);
    try {
      const updated = await SalesInvoiceService.issue(invoice.id);
      setInvoice(updated);
      toast.showSuccess('Invoice issued');
      setIssueDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to issue invoice');
    } finally {
      setIssuing(false);
    }
  };

  const handleDelete = async () => {
    if (!invoice) return;
    setDeleting(true);
    try {
      await SalesInvoiceService.remove(invoice.id);
      toast.showSuccess('Invoice deleted');
      navigate('/sales/invoices');
    } catch (e) {
      toast.showApiError(e, 'Failed to delete invoice');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleVoid = async () => {
    if (!invoice || !voidReason.trim()) return;
    setVoiding(true);
    try {
      const updated = await SalesInvoiceService.voidInvoice(invoice.id, { voidReason: voidReason.trim() });
      setInvoice(updated);
      toast.showSuccess('Invoice voided');
      setVoidDialogOpen(false);
      setVoidReason('');
    } catch (e) {
      toast.showApiError(e, 'Failed to void invoice');
    } finally {
      setVoiding(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoice) return;
    setDownloading(true);
    try {
      const blob = await SalesInvoiceService.downloadPdf(invoice.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoice.invoiceNumber ?? invoice.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.showApiError(e, 'Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const handleEmail = async () => {
    if (!invoice) return;
    setEmailing(true);
    try {
      await SalesInvoiceService.email(invoice.id);
      toast.showSuccess('Invoice emailed successfully');
    } catch (e) {
      toast.showApiError(e, 'Failed to email invoice');
    } finally {
      setEmailing(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!invoice) return <Box sx={{ p: 3 }}><Typography>Invoice not found.</Typography></Box>;

  const status: SalesInvoiceStatus = invoice.status;
  const balance = invoice.totalAmount - invoice.paidAmount;

  // Status-gated action map
  const can = {
    issue: status === 'DRAFT',
    editDraft: status === 'DRAFT',
    delete: status === 'DRAFT',
    recordReceipt: status === 'ISSUED' || status === 'PARTIALLY_PAID',
    void: status === 'ISSUED' || status === 'PARTIALLY_PAID',
    edit: status === 'ISSUED' || status === 'PARTIALLY_PAID',
    pdf: status !== 'DRAFT',
    email: status !== 'DRAFT',
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Title + action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          {invoice.invoiceNumber ?? 'Draft Invoice'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {can.issue && (
            <Button variant="contained" color="primary" onClick={() => setIssueDialogOpen(true)}>
              Issue
            </Button>
          )}
          {(can.editDraft || can.edit) && (
            <Button variant="outlined" onClick={() => navigate(`/sales/invoices/${invoice.id}/edit`)}>
              Edit
            </Button>
          )}
          {can.recordReceipt && (
            <Button variant="contained" color="success" onClick={() => setReceiptDialogOpen(true)}>
              Record Receipt
            </Button>
          )}
          {can.pdf && (
            <Button variant="outlined" onClick={handleDownloadPdf} disabled={downloading}>
              {downloading ? <CircularProgress size={20} /> : 'Download PDF'}
            </Button>
          )}
          {can.email && (
            <Button variant="outlined" onClick={handleEmail} disabled={emailing}>
              {emailing ? <CircularProgress size={20} /> : 'Email'}
            </Button>
          )}
          {can.void && (
            <Button variant="outlined" color="error" onClick={() => setVoidDialogOpen(true)}>
              Void
            </Button>
          )}
          {can.delete && (
            <Button variant="outlined" color="error" onClick={() => setDeleteDialogOpen(true)}>
              Delete
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      {/* Header info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Invoice Number</Typography>
            <Typography>{invoice.invoiceNumber ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
              <Chip label={status} color={STATUS_COLORS[status]} size="small" />
              {invoice.overdue && (
                <Chip label="Overdue" color="error" size="small" />
              )}
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Customer</Typography>
            <Typography>{invoice.customerName ?? invoice.customerId}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Issue Date</Typography>
            <Typography>{invoice.issueDate}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Due Date</Typography>
            <Typography>{invoice.dueDate}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography>{invoice.subtotal.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Tax Amount</Typography>
            <Typography>{invoice.taxAmount.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total Amount</Typography>
            <Typography fontWeight="bold">{invoice.totalAmount.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
            <Typography>{invoice.paidAmount.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Balance Due</Typography>
            <Typography fontWeight="bold" color={balance > 0 ? 'error.main' : 'text.primary'}>
              {balance.toFixed(2)}
            </Typography>
          </Box>
          {invoice.notes && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Notes</Typography>
              <Typography>{invoice.notes}</Typography>
            </Box>
          )}
          {invoice.journalEntryId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Journal Entry</Typography>
              <Link
                component={RouterLink}
                to={`/accounting/journal-entries/${invoice.journalEntryId}`}
              >
                View JE
              </Link>
            </Box>
          )}
          {invoice.reversalJournalEntryId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Reversal Journal Entry</Typography>
              <Link
                component={RouterLink}
                to={`/accounting/journal-entries/${invoice.reversalJournalEntryId}`}
              >
                View Reversal JE
              </Link>
            </Box>
          )}
          {status === 'VOIDED' && invoice.voidReason && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Void Reason</Typography>
              <Typography color="error.main">{invoice.voidReason}</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Line items table */}
      {invoice.lines.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Line Items</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="right">Tax %</TableCell>
                <TableCell align="right">Line Tax</TableCell>
                <TableCell align="right">Line Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.lines.map(line => (
                <TableRow key={line.id}>
                  <TableCell>{line.description}</TableCell>
                  <TableCell align="right">{line.quantity}</TableCell>
                  <TableCell align="right">{line.unitPrice.toFixed(2)}</TableCell>
                  <TableCell align="right">{line.taxRatePct}%</TableCell>
                  <TableCell align="right">{line.lineTax?.toFixed(2) ?? '—'}</TableCell>
                  <TableCell align="right">{line.lineTotal?.toFixed(2) ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Receipt history */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Receipt History</Typography>
        {receipts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No receipts recorded.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Receipt #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receipts.map(r => (
                <TableRow key={r.id}>
                  <TableCell>
                    <Link component={RouterLink} to={`/sales/receipts/${r.id}`}>
                      {r.receiptNumber ?? r.id}
                    </Link>
                  </TableCell>
                  <TableCell>{r.receiptDate}</TableCell>
                  <TableCell align="right">{r.amount.toFixed(2)}</TableCell>
                  <TableCell>{r.method}</TableCell>
                  <TableCell>
                    {r.status === 'REVERSED' ? (
                      <Chip label="Reversed" color="error" size="small" />
                    ) : (
                      <Chip label="Posted" color="success" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Issue confirm dialog */}
      <Dialog open={issueDialogOpen} onClose={() => setIssueDialogOpen(false)}>
        <DialogTitle>Issue this invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Issuing the invoice will post a journal entry and assign an invoice number.
            The invoice can no longer be deleted after issuance.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueDialogOpen(false)} disabled={issuing}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleIssue} disabled={issuing}>
            {issuing ? <CircularProgress size={20} /> : 'Confirm Issue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete this draft invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the draft invoice. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void dialog — collects voidReason (ReverseConfirmDialog has no reason field) */}
      <Dialog open={voidDialogOpen} onClose={() => setVoidDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Void this invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Voiding the invoice will post a reversal journal entry. This cannot be undone.
            Any outstanding balance will be cleared. A void reason is required.
          </DialogContentText>
          <TextField
            label="Void Reason"
            value={voidReason}
            onChange={e => setVoidReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setVoidDialogOpen(false); setVoidReason(''); }} disabled={voiding}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleVoid}
            disabled={voiding || voidReason.trim().length === 0}
          >
            {voiding ? <CircularProgress size={20} /> : 'Confirm Void'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Receipt dialog */}
      {receiptDialogOpen && (
        <RecordReceiptDialog
          open={receiptDialogOpen}
          invoice={invoice}
          onClose={() => setReceiptDialogOpen(false)}
          onPosted={async () => {
            setReceiptDialogOpen(false);
            await reload();
          }}
        />
      )}

    </Box>
  );
};

export default SalesInvoiceDetail;
